/**
 * Minimal 3D Test Page - Direct R3F test without Rapier physics
 */

import { useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function RotatingCube() {
  const meshRef = useRef();
  
  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.5;
      meshRef.current.rotation.y += delta * 0.5;
    }
  });
  
  return (
    <mesh ref={meshRef} position={[0, 0, 0]}>
      <boxGeometry args={[2, 2, 2]} />
      <meshStandardMaterial color="orange" />
    </mesh>
  );
}

function SimpleScene() {
  const { scene } = useThree();
  
  // Set background properly
  scene.background = new THREE.Color('#87CEEB');
  
  return (
    <>
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <RotatingCube />
      <mesh position={[0, -2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[20, 20]} />
        <meshStandardMaterial color="#4a7c4f" />
      </mesh>
    </>
  );
}

export default function MinimalTestPage() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  
  useEffect(() => {
    // Simple WebGL check
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
      if (!gl) {
        setError('WebGL not supported');
      }
    } catch (e) {
      setError(e.message);
    }
  }, []);
  
  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">WebGL Error: {error}</p>
          <button
            onClick={() => navigate('/world/midgard')}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg"
          >
            Return to Map
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black" data-testid="minimal-test-page">
      <header className="absolute top-4 left-4 z-10">
        <button
          onClick={() => navigate('/world/midgard')}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm border border-amber-900/30"
        >
          <ArrowLeft className="w-5 h-5 text-amber-100" />
          <span className="text-amber-100">Back</span>
        </button>
      </header>
      
      <div className="absolute top-4 right-4 z-10 px-4 py-2 bg-black/50 rounded-lg text-amber-100 text-sm">
        {loaded ? 'R3F Canvas Loaded' : 'Loading...'}
      </div>
      
      <Canvas
        camera={{ position: [5, 5, 5], fov: 60 }}
        gl={{ antialias: true }}
        onCreated={() => setLoaded(true)}
      >
        <SimpleScene />
      </Canvas>
    </div>
  );
}
